import React, { createContext, useMemo } from 'react';
import { useDecision } from '@optimizely/react-sdk';
import { useTrackOptimizely } from '@fff-web/fff-analytics';
import defaultReactivateMyMembershipContext, {
  REACTIVATE_MEMBERSHIP_EVERGREEN_CONTENT,
} from './defaultReactivateMyMembershipContext';
import { GROWTH_MY_REACTIVATE_MEMBERSHIP } from '../../../utils/Optimizely/featureFlagVariations';
import { useUserContext } from '@fff-web/fff-utilities';
import { useHistory, useLocation } from 'react-router-dom';
import { redirectToBaseSite } from '../../../utils/redirectMethods';
import isEmpty from 'lodash/isEmpty';

export const ReactivateMyMembershipContext = createContext(defaultReactivateMyMembershipContext);

export const ReactivateMyMembershipProvider = ({ children }: { children: React.ReactNode }) => {
  const history = useHistory();
  const { search } = useLocation();
  const { userInfo } = useUserContext();

  const subscriptionState = userInfo?.user?.subscriptionState;
  const { loggedIn } = userInfo;

  if (loggedIn && subscriptionState === 'CANCELED') {
    history.replace(`/reactivate-today${search}`);
    return null;
  }

  if (loggedIn && subscriptionState !== 'EXPIRED') {
    redirectToBaseSite();
    return null;
  }

  const [growthMyMembershipDecision] = useDecision(GROWTH_MY_REACTIVATE_MEMBERSHIP.FEATURE_FLAG);

  useTrackOptimizely(growthMyMembershipDecision, GROWTH_MY_REACTIVATE_MEMBERSHIP.EXPERIMENT_NAME);

  const variables = !isEmpty(growthMyMembershipDecision.variables)
    ? growthMyMembershipDecision.variables
    : REACTIVATE_MEMBERSHIP_EVERGREEN_CONTENT;

  const memoizedValues = useMemo(
    () => ({ growthMyMembership: { ...growthMyMembershipDecision, variables } }),
    [growthMyMembershipDecision],
  );

  return (
    <ReactivateMyMembershipContext.Provider value={memoizedValues}>
      {children}
    </ReactivateMyMembershipContext.Provider>
  );
};

export function useReactivateMyMembershipContext() {
  const context = React.useContext(ReactivateMyMembershipContext);
  if (!context) {
    throw new Error(
      'You must use the ReactivateMyMembershipProvider in order to consume this context.',
    );
  }
  return context;
}

export default ReactivateMyMembershipProvider;
